// ScheduleModal.js
import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import dayjs from "dayjs";
const ScheduleModal = ({ open, onClose, onSelect }) => {
  const SAFE_OFFSET_MINUTES = 1;
  const [dateTime, setDateTime] = useState(dayjs().add(SAFE_OFFSET_MINUTES, "minute"));
  const [minDateTimeStr, setMinDateTimeStr] = useState(
    dayjs().add(SAFE_OFFSET_MINUTES, "minute").format("YYYY-MM-DDTHH:mm")
  );
  const [touched, setTouched] = useState(false);
  const dateInputRef = useRef(null);

  // update minDateTimeStr every 15 seconds so the input's min stays current
  useEffect(() => {
    const updateMin = () => {
      const nextMin = dayjs().add(SAFE_OFFSET_MINUTES, "minute");
      setMinDateTimeStr(nextMin.format("YYYY-MM-DDTHH:mm"));
      // If current selected datetime falls now-before-min, clamp it
      if (dateTime.isBefore(nextMin)) {
        setDateTime(nextMin);
      }
    };

    updateMin(); // init
    const id = setInterval(updateMin, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SAFE_OFFSET_MINUTES]);

  // helper: returns true if selected dateTime is valid (>= min)
  const isValid = (dt = dateTime) => {
    const min = dayjs(minDateTimeStr);
    return dt && (dt.isSame(min) || dt.isAfter(min));
  };

  // handlers
  const handleIconClick = () => {
    if (dateInputRef.current && dateInputRef.current.showPicker) {
      dateInputRef.current.showPicker();
    } else if (dateInputRef.current) {
      dateInputRef.current.focus();
    }
  };

  const handleInputClick = () => {
    setTouched(true);
    if (dateInputRef.current && dateInputRef.current.showPicker) {
      dateInputRef.current.showPicker();
    }
  };

  const handleChange = (e) => {
    setTouched(true);
    const val = e.target.value;
    if (!val) return;
    // If parsed is before min, clamp to min (but keep user informed)
    const min = dayjs(minDateTimeStr);
    const parsed = dayjs(val);
    if (parsed.isBefore(min)) {
      setDateTime(min);
    } else {
      setDateTime(parsed);
    }
  };

  const handleUseNow = () => {
    const nowPlus = dayjs().add(SAFE_OFFSET_MINUTES, "minute");
    setDateTime(nowPlus);
    setTouched(true);
  };

  const handleDialogClose = (event, reason) => {
    // keep your behavior: don't close on backdrop/escape
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    onClose();
  };

  const handleSchedule = () => {
    // final safety check
    const min = dayjs(minDateTimeStr);
    if (!dateTime || dateTime.isBefore(min)) {
      // clamp and don't call onSelect with invalid date
      setDateTime(min);
      setTouched(true);
      return;
    }
    onSelect(dateTime);
  };

  // UI helpers
  const formattedPreview = dateTime ? dateTime.format("dddd, MMM D, YYYY [at] h:mm A") : "";

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{ sx: { borderRadius: 2, p: 3, minWidth: 360 } }}
    >
      <Typography sx={{ fontSize: "18px", color: "#000", fontWeight: "600", mb: 1 }}>
        Schedule Message
      </Typography>

      <DialogContent sx={{ px: 0 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <input
            ref={dateInputRef}
            type="datetime-local"
            value={dateTime.format("YYYY-MM-DDTHH:mm")}
            min={minDateTimeStr}
            max={dayjs().add(2, "month").format("YYYY-MM-DDTHH:mm")}
            onChange={handleChange}
            onClick={handleInputClick}
            aria-label="Schedule date and time"
            style={{
              flex: 1,
              padding: "10.5px 14px",
              paddingRight: "18px",
              borderRadius: "50px",
              border: touched && !isValid() ? "2px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.23)",
              fontSize: "16px",
              fontFamily: "inherit",
              backgroundColor: "transparent",
              cursor: "pointer",
              outline: "none",
              boxSizing: "border-box",
              WebkitAppearance: "none",
              MozAppearance: "textfield",
              appearance: "none",
            }}
            onMouseOver={(e) => {
              e.target.style.borderColor = "#916008";
            }}
            onMouseOut={(e) => {
              e.target.style.borderColor = touched && !isValid() ? "#d32f2f" : "rgba(0, 0, 0, 0.23)";
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#916008";
              e.target.style.borderWidth = "2px";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = touched && !isValid() ? "#d32f2f" : "rgba(0, 0, 0, 0.23)";
              e.target.style.borderWidth = "1px";
            }}
          />

          {/* calendar icon */}
          <Box
            onClick={handleIconClick}
            sx={{
              position: "absolute",
              right: "56px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              background: "#fff",
              height: "40px",
              borderRadius: "50%",
              zIndex: 999,
            }}
          >
            <CalendarMonthIcon sx={{ color: "rgba(0, 0, 0, 0.54)", fontSize: "24px" }} />
          </Box>

          {/* Use Now button */}
          <Tooltip title="Set to current time (with safe offset)">
            <IconButton
              onClick={handleUseNow}
              aria-label="Use now"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                border: "1px solid rgba(0,0,0,0.12)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                ml: "auto",
              }}
            >
              <AccessTimeIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            display: "block",
            color: touched && !isValid() ? "#d32f2f" : "text.secondary",
            textAlign: "center",
          }}
        >
          {touched && !isValid()
            ? `Selected time is in the past. Earliest allowed: ${dayjs(minDateTimeStr).format(
              "MMM D, YYYY h:mm A"
            )}`
            : "Click the calendar icon or input to open picker. Use the clock icon to set current time."}
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            textAlign: "center",
            color: "text.secondary",
            fontSize: 14,
          }}
        >
          Preview:{" "}
          <Typography component="span" sx={{ color: "#000", fontWeight: 600 }}>
            {formattedPreview}
          </Typography>
        </Typography>
      </DialogContent>

      <DialogActions sx={{ gap: 2, pt: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            fontSize: "16px",
            flex: 1,
            textTransform: "capitalize",
            color: "#757575",
            background: "#fff",
            border: "1px solid #757575",
            borderRadius: "50px",
            "&:hover": { background: "#f5f5f5" },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSchedule}
          disabled={!isValid()}
          sx={{
            fontSize: "16px",
            flex: 1,
            textTransform: "capitalize",
            color: "#fff",
            background: !isValid() ? "rgba(145,96,8,0.5)" : "#916008",
            borderRadius: "50px",
            "&:hover": { background: !isValid() ? "rgba(145,96,8,0.5)" : "#7a5007" },
          }}
        >
          Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleModal;
